"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.objectType = void 0;
const caster_1 = require("../caster");
const actual_type_1 = require("./actual-type");
const caster_cache_1 = require("./caster-cache");
function objectType(declaration, validateOptions, circularCache = caster_cache_1.createCache()) {
    if (declaration === null || typeof declaration !== 'object') {
        throw new Error('First argument (declaration) must be an Object.');
    }
    const circularCaster = circularCache.get(declaration);
    if (circularCaster !== undefined) {
        return circularCaster;
    }
    const { type, required, additionalProperties, trim } = declaration;
    let { properties, patternProperties } = declaration;
    if (type !== 'Object') {
        throw new Error('"type" property on declaration must be "Object"');
    }
    if (required !== undefined && typeof required !== 'boolean') {
        throw new Error('"required" property on declaration must be undefined or a Boolean');
    }
    const defaultCandidate = Reflect.get(declaration, 'default');
    if (defaultCandidate !== undefined &&
        (defaultCandidate === null || typeof defaultCandidate !== 'object' || Object.keys(defaultCandidate).length !== 0)) {
        throw new Error('"default" property on declaration must be undefined or an empty Object');
    }
    if (properties !== undefined && (properties === null || typeof properties !== 'object')) {
        throw new Error('"properties" property on declaration must be undefined or an Object');
    }
    if (properties === undefined) {
        properties = {};
    }
    if (patternProperties !== undefined && (patternProperties === null || typeof patternProperties !== 'object')) {
        throw new Error('"patternProperties" property on declaration must be undefined or an Object');
    }
    if (patternProperties === undefined) {
        patternProperties = {};
    }
    if (trim !== undefined && typeof trim !== 'boolean') {
        throw new Error('"trim" property on declaration must be undefined or a Boolean');
    }
    if (Object.values(properties).every((value) => value.sourceKey === undefined || typeof value.sourceKey === 'string') ===
        false) {
        throw new Error('"sourceKey" on "properties" property on declaration must be undefined or a String');
    }
    if (additionalProperties !== undefined && typeof additionalProperties !== 'boolean') {
        throw new Error('"additionalProperties" property on declaration must undefined or a Boolean');
    }
    let caster = (candidate) => ({ type, isValid: true, invalidations: [], candidate });
    const keyPatterns = Object.keys(patternProperties).reduce((acc, keyPattern) => {
        acc[keyPattern] = new RegExp(keyPattern);
        return acc;
    }, {});
    const isPatternKey = (key) => Object.values(keyPatterns).some((keyPattern) => keyPattern.test(key));
    if (additionalProperties !== true) {
        const propertiesKeys = Object.keys(properties);
        const sourceKeys = Object.values(properties)
            .filter((propValue) => propValue.sourceKey !== null && propValue.sourceKey !== undefined)
            .map((propValue) => propValue.sourceKey);
        const casterRef = caster;
        caster = (candidate) => {
            const result = casterRef(candidate);
            const additionalKeys = Object.keys(candidate).filter((key) => propertiesKeys.includes(key) === false && sourceKeys.includes(key) === false && isPatternKey(key) === false);
            if (additionalKeys.length !== 0) {
                return {
                    ...result,
                    invalidations: [[[], 'Object', 'additionalProperties', false, additionalKeys], ...result.invalidations]
                };
            }
            return result;
        };
    }
    let propsCaster;
    let patternPropsCaster;
    const casterRef = (candidate, register = new Map()) => {
        if ((candidate === undefined || candidate === null) && defaultCandidate !== undefined) {
            candidate = defaultCandidate;
        }
        let declarationRegister = register.get(declaration);
        if (declarationRegister === undefined) {
            declarationRegister = new Set();
            register.set(declaration, declarationRegister);
        }
        else if (declarationRegister.has(candidate)) {
            return {
                type,
                isValid: true,
                invalidations: [],
                candidate
            };
        }
        if (candidate === undefined || candidate === null) {
            if (required === false) {
                return {
                    type,
                    isValid: true,
                    invalidations: [],
                    candidate
                };
            }
            else {
                return {
                    type,
                    isValid: false,
                    candidate,
                    invalidations: [[[], 'Object', 'required', true, candidate]]
                };
            }
        }
        const candidateType = actual_type_1.ctorName(candidate);
        if (candidate === null ||
            candidate === undefined ||
            typeof candidate !== 'object' ||
            Array.isArray(candidate) ||
            candidateType === 'Date' ||
            candidateType === 'RegExp' ||
            candidateType === 'Buffer') {
            return {
                type,
                isValid: false,
                candidate,
                invalidations: [[[], 'Object', 'type', type, candidateType]]
            };
        }
        declarationRegister.add(candidate);
        const preparedCandidate = {
            ...Object.entries(candidate).reduce((acc, [key, value]) => {
                if (isPatternKey(key)) {
                    acc[key] = value;
                }
                return acc;
            }, {}),
            ...Object.entries(properties).reduce((acc, [key, value]) => {
                if (value.sourceKey !== undefined && value.sourceKey !== null) {
                    if (Reflect.has(candidate, value.sourceKey)) {
                        acc[key] = candidate[value.sourceKey];
                    }
                }
                else if (Reflect.has(candidate, key)) {
                    acc[key] = candidate[key];
                }
                return acc;
            }, trim === true ? {} : { ...candidate })
        };
        const result = caster(preparedCandidate);
        const propertiesKeys = Object.keys(properties);
        const patternPropertiesValidation = Object.entries(result.candidate).reduce((acc, [candidatePropKey, candidatePropValue]) => {
            if (propertiesKeys.includes(candidatePropKey)) {
                return acc;
            }
            const matchingKeyPatterns = Object.entries(keyPatterns)
                .filter(([keyPattern, keyRegExp]) => keyRegExp.test(candidatePropKey))
                .map(([keyPattern]) => keyPattern);
            if (matchingKeyPatterns.length === 0) {
                return acc;
            }
            const propResults = matchingKeyPatterns.map((keyPattern) => patternPropsCaster[keyPattern](candidatePropValue, register));
            const propValidResult = propResults.find((propResult) => propResult.isValid);
            if (propValidResult === undefined) {
                return {
                    ...acc,
                    [candidatePropKey]: propResults[0]
                };
            }
            else {
                result.candidate[candidatePropKey] = propValidResult.candidate;
                return acc;
            }
        }, {});
        const propertiesValidation = Object.entries(propsCaster).reduce((acc, [propKey, propCaster]) => {
            const propResult = propCaster(result.candidate[propKey], register);
            if (Reflect.has(result.candidate, propKey) || propResult.candidate !== undefined) {
                result.candidate[propKey] = propResult.candidate;
            }
            if (propResult.isValid === false) {
                acc[propKey] = propResult;
            }
            return acc;
        }, {});
        // const invalidPatternPropsKeys = Object.keys(patternPropertiesValidation).sort()
        // if (invalidPatternPropsKeys.length !== 0) {
        //   result.invalidations.push({
        //     rule: 'patternProperties',
        //     setting: declaration.patternProperties,
        //     value: invalidPatternPropsKeys
        //   })
        // }
        // const invalidPropsKeys = Object.keys(propertiesValidation).sort()
        // if (invalidPropsKeys.length !== 0) {
        //   result.invalidations.push({
        //     rule: 'properties',
        //     setting: declaration.properties,
        //     value: invalidPropsKeys
        //   })
        // }
        result.properties = {
            ...patternPropertiesValidation,
            ...propertiesValidation
        };
        const invalidations = Object.entries(result.properties).reduce((acc, [key, propResult]) => {
            return [...acc, ...propResult.invalidations.map(([path, ...rest]) => [[key, ...path], ...rest])];
        }, result.invalidations);
        return {
            type,
            isValid: invalidations.length === 0,
            invalidations,
            candidate: invalidations.length === 0 ? result.candidate : candidate
        };
    };
    // const casterRefCache = (candidate, cache = new WeakMap()) => {
    //   const cachedResult = cache.get(candidate)
    //   if (cachedResult !== undefined) {
    //     return cachedResult
    //   }
    //   const result = casterRef(candidate)
    //   if (candidate !== null && typeof candidate === 'object') {
    //     cache.set(candidate, result)
    //   }
    //   return result
    // }
    circularCache.set(declaration, casterRef);
    propsCaster = Object.entries(properties).reduce((acc, [propKey, propDeclaration]) => {
        acc[propKey] = caster_1.createCaster(propDeclaration, validateOptions, circularCache);
        return acc;
    }, {});
    patternPropsCaster = Object.entries(patternProperties).reduce((acc, [propKey, propDeclaration]) => {
        acc[propKey] = caster_1.createCaster(propDeclaration, validateOptions, circularCache);
        return acc;
    }, {});
    return casterRef;
}
exports.objectType = objectType;
